// ═══════════════════════════════════════════════════════════════
// Debris Portal — Overview Dashboard (all zones for incident)
// No zone selection required — tap a zone card to select it.
// ═══════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { useDebrisStore } from '../../../store/debrisStore';
import { useDebrisOpsStore } from '../../../store/debrisOpsStore';
import { useAuthStore } from '../../../store/authStore';
import { DEBRIS_TYPE_LABELS, DEBRIS_TYPE_COLORS } from '../../../types/debris';
import type { DebrisZone } from '../../../types/debris';
import { IconTruck, IconTicket, IconMap2, IconPackage, IconChevronRight } from '@tabler/icons-react';

interface Props {
  onSelectZone: (z: DebrisZone) => void;
}

const STATUS_COLOR: Record<string, string> = {
  in_transit: '#d97706', delivered: '#059669', verified: '#2563eb',
  draft: '#939598', voided: '#B12525',
};

export default function DebrisPortalDashboard({ onSelectZone }: Props) {
  const { activeIncident } = useAuthStore();
  const { zones, trucks } = useDebrisStore();
  const { loadTickets } = useDebrisOpsStore();

  const today = new Date().toISOString().slice(0, 10);

  // Aggregate all-incident stats
  const allTickets = useMemo(
    () => loadTickets.filter(t => activeIncident ? t.incidentId === String(activeIncident.id) : true),
    [loadTickets, activeIncident],
  );

  const todayTickets  = allTickets.filter(t => (t.pickupTime || '').startsWith(today));
  const inTransit     = allTickets.filter(t => t.status === 'in_transit');
  const verified      = allTickets.filter(t => t.status === 'verified' || t.status === 'delivered');
  const totalCY       = allTickets.reduce((s, t) => s + (parseFloat(t.calculatedCubicYards) || 0), 0);
  const activeTrucks  = trucks.filter(t => t.status === 'certified' || t.status === 'standby');

  // CY breakdown by debris type (top 5)
  const cyByType = useMemo(() => {
    const map: Record<string, number> = {};
    allTickets.forEach(t => {
      if (t.debrisType && t.calculatedCubicYards) {
        map[t.debrisType] = (map[t.debrisType] || 0) + (parseFloat(t.calculatedCubicYards) || 0);
      }
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [allTickets]);

  const maxCY = cyByType[0]?.[1] || 1;

  // Zone-level stats
  const zoneStats = useMemo(() => zones.map(z => {
    const zt = allTickets.filter(t => t.zoneId === String(z.id));
    const todayZt = zt.filter(t => (t.pickupTime || '').startsWith(today));
    return {
      zone: z,
      ticketCount: zt.length,
      todayCount: todayZt.length,
      inTransit: zt.filter(t => t.status === 'in_transit').length,
      actualCY: parseFloat(z.actualCubicYards) || 0,
      estimatedCY: parseFloat(z.estimatedCubicYards) || 0,
    };
  }), [zones, allTickets, today]);

  return (
    <div>
      {/* Incident header */}
      <div style={{
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        borderRadius: 4, padding: '16px 16px', marginBottom: 16, color: '#fff',
      }}>
        <div style={{ fontSize: 12, color: '#939598', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
          Active Incident
        </div>
        <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 2 }}>
          {activeIncident?.name || '—'}
        </div>
        <div style={{ fontSize: 12, color: '#939598' }}>
          {zones.length} zone{zones.length !== 1 ? 's' : ''} · {activeTrucks.length} certified truck{activeTrucks.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* KPI row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
        {[
          { label: 'In Transit',    value: inTransit.length,          icon: IconTruck,    color: '#d97706' },
          { label: 'Today Tickets', value: todayTickets.length,        icon: IconTicket,   color: '#4B3FFF' },
          { label: 'Total CY',      value: `${totalCY.toLocaleString()}`, icon: IconPackage,  color: '#059669' },
          { label: 'Verified',      value: verified.length,            icon: IconMap2,     color: '#2563eb' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} style={{
            background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 4,
            padding: '16px 16px', borderTop: `3px solid ${color}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <Icon size={14} style={{ color }} />
              <span style={{ fontSize: 12, color: '#7B7D7F', fontWeight: 600 }}>{label}</span>
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#1B1C1C' }}>{value}</div>
          </div>
        ))}
      </div>

      {/* CY by debris type */}
      {cyByType.length > 0 && (
        <div className="sp-card">
          <div className="sp-card-title">CY by Debris Type</div>
          {cyByType.map(([type, cy]) => {
            const color = DEBRIS_TYPE_COLORS[type] || '#7B7D7F';
            const pct = Math.round((cy / maxCY) * 100);
            return (
              <div key={type} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                  <span style={{ color: '#475569', fontWeight: 500 }}>{DEBRIS_TYPE_LABELS[type] || type}</span>
                  <span style={{ color: '#7B7D7F', fontWeight: 600 }}>{cy.toFixed(1)} CY</span>
                </div>
                <div style={{ height: 6, background: '#e2e8f0', borderRadius: 99 }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 99 }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Zone cards */}
      <div style={{ marginTop: 4 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#1B1C1C', marginBottom: 8 }}>
          Zones ({zones.length})
        </div>
        {zones.length === 0 && (
          <div style={{ textAlign: 'center', color: '#939598', fontSize: 14, padding: '24px 0' }}>
            No zones found for this incident.
          </div>
        )}
        {zoneStats.map(({ zone: z, ticketCount, todayCount, inTransit: zit, actualCY, estimatedCY }) => {
          const pct = estimatedCY > 0 ? Math.min(Math.round((actualCY / estimatedCY) * 100), 100) : 0;
          const statusColor = z.status === 'active' ? '#059669' : z.status === 'pending' ? '#d97706' : '#939598';
          return (
            <div key={z.id}
              onClick={() => onSelectZone(z)}
              style={{
                background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 4,
                padding: '16px 16px', marginBottom: 8, cursor: 'pointer',
                borderLeft: `4px solid ${statusColor}`,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#1B1C1C' }}>
                    {z.zoneName || z.zoneCode}
                  </span>
                  <span style={{
                    fontSize: 12, fontWeight: 700, color: statusColor,
                    background: `${statusColor}18`, padding: '2px 8px', borderRadius: 99,
                  }}>
                    {z.status}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#7B7D7F', marginBottom: 8 }}>
                  Pass {z.currentPass || '1'}
                  {ticketCount > 0 && ` · ${ticketCount} ticket${ticketCount !== 1 ? 's' : ''}`}
                  {todayCount > 0 && ` · ${todayCount} today`}
                  {zit > 0 && (
                    <span style={{ color: '#d97706', fontWeight: 600 }}> · {zit} in transit</span>
                  )}
                </div>
                {estimatedCY > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, height: 5, background: '#e2e8f0', borderRadius: 99 }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: '#d97706', borderRadius: 99 }} />
                    </div>
                    <span style={{ fontSize: 12, color: '#7B7D7F', whiteSpace: 'nowrap' }}>
                      {actualCY.toLocaleString()}/{estimatedCY.toLocaleString()} CY
                    </span>
                  </div>
                )}
              </div>
              <IconChevronRight size={16} style={{ color: '#939598', flexShrink: 0 }} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
